import { DayOfWeek, LocalTime } from "@js-joda/core";
import h from "trader-hyperscript";
import { rpc } from "trader.ts/network";
import { Source } from "trader.ts/types/source";
import { dyn } from "trader.ts/ui";
import { Form, constantField, selectBox } from "trader.ts/ui.forms";
import { checkAllCasesHandled } from "trader.ts/utils";
import {
  formulaid,
  instrumentid,
  internalteacherid,
  lessongroupid,
  planningmode,
  studentid,
} from "../../../sql/classy/types";
import * as FA from "../../../styles/fontawesome.css.classes";
import * as style from "../../../styles/style.css.classes";
import { radioField } from "../../client/radiofield";
import { renderSaveButton } from "../../client/savebutton";
import { formulaType } from "../../parsing_from_ur";
import {
  renderFirstNameLastName,
  renderHeaderItemWithMusicNote,
} from "../../render";
import * as routes from "../../routes";
import { stringToJsxClientside } from "../editemailtemplates/client";
import {
  makeNewStudentDataForm,
  makeNewStudentLoginForm,
} from "../public_new_enrollment/client";

type opts = {
  subject:
    | { tag: "private"; instrument: { description: string; id: instrumentid } }
    | {
        tag: "lessongroup";
        lessongroupid: lessongroupid;
        lessongroup: { description: { title: string; content: string }[] };
      };
  students: null | { id: studentid; firstname: string; lastname: string }[];
  schoolname: string;
  termsurl: string | null;
  requireConfirmationForVisualMediaPublicity: boolean;
  allFormulas: {
    id: formulaid;
    type_: formulaType;
    planningmode: planningmode;
    description: string;
    showonpublicplanning: boolean;
    instruments: instrumentid[] | null;
  }[];
};

const mainStyle =
  "max-width: 800px; padding: 32px 16px; background-color: transparent;";

export function render(opts: opts): HTMLElement | HTMLElement[] {
  const header = (
    <div
      className={
        style.full_width_center_800 + style.full_width_grey_top_section
      }
    >
      <section>
        {
          Array.from(
            stringToJsxClientside(
              opts.subject.tag === "private"
                ? "<div>Zet je vandaag op de <b>wachtlijst voor de cursus %instrument</b> en onze medewerkers contacteren je wanneer er een plaats vrijkomt.</div>".replace("%instrument", opts.subject.instrument.description)
                : "<div>Zet je vandaag op de <b>wachtlijst</b> en onze medewerkers contacteren je wanneer er een plaats vrijkomt.</div>",
            ).childNodes,
          ) as HTMLElement[]
        }
      </section>

      {opts.subject.tag === "lessongroup" ? (
        <section>
          {opts.subject.lessongroup.description.map((d) =>
            renderHeaderItemWithMusicNote(d.title, d.content),
          )}
        </section>
      ) : (
        []
      )}
    </div>
  );

  const loginRouteLink = routes.loginRoute.link({
    redirecturl: routes.waitinglist_studentRoute.link({
      privateorgroup:
        opts.subject.tag === "private"
          ? { tag: "private", instrumentid: opts.subject.instrument.id }
          : opts.subject.tag === "lessongroup"
          ? { tag: "lessongroup", lessongroupid: opts.subject.lessongroupid }
          : checkAllCasesHandled(opts.subject),
    }),
    redirectdescription: "Log je in om op de wachtlijst in te schrijven",
    logoutfirst: true, // necessary if you're eg. logged in as teacher or admin
  });

  const content = (() => {
    if (opts.students === null) {
      // This means we're not logged in
      const newStudentS = new Source(false);
      return dyn(newStudentS, (newStudent) => {
        if (newStudent === false) {
          return (
            <section
              className={style.equal_columns}
              style="padding: 8px; align-items: stretch;"
            >
              <button
                className={style.button_accent}
                onclick={() => newStudentS.set(true)}
                style="width: 100%; height: 4rem;"
              >
                {"Ik ben nieuw bij %schoolname".replace(
                  "%schoolname",
                  opts.schoolname,
                )}
              </button>
              <a className={style.a_around_button} href={loginRouteLink}>
                <button style="width: 100%; height: 4rem;">
                  {"Ik heb al een account bij %schoolname".replace(
                    "%schoolname",
                    opts.schoolname,
                  )}
                </button>
              </a>
            </section>
          );
        } else {
          const dataForm = makeNewStudentDataForm({
            termsurl: opts.termsurl,
            requireConfirmationForVisualMediaPublicity:
              opts.requireConfirmationForVisualMediaPublicity,
          });
          const loginForm = makeNewStudentLoginForm({
            showFinancialForm: false,
          });
          const wlForm = makeWaitinglistForm(opts);

          //
          // !!
          // Lots of copy paste from public_new_enrollment
          // !!
          //

          const saveButton = renderSaveButton({
            save: async function save() {
              const dataValuesP = dataForm.s.get();
              const wlFormP = wlForm.s.get();
              const loginValuesP = loginForm.s.get();

              if (
                dataValuesP.tag !== "parsed" ||
                wlFormP.tag !== "parsed" ||
                loginValuesP.tag !== "parsed"
              ) {
                return {
                  error: "Je mist nog een of meer verplichte velden",
                };
              }
              const dataValues = dataValuesP.parsed;
              const loginValues = loginValuesP.parsed;
              const wlValues = wlFormP.parsed;
              if (dataValues.agreedToTerms !== true) {
                return {
                  error: "Je moet de algemene voorwaarden goedkeuren",
                };
              }

              const emails = [loginValues.email]
                .concat(loginValues.extra_emails)
                .map((em) => em.trim())
                .uniq();

              return rpc(routes.saveWaitinglistNewStudentSpec, undefined, {
                subject: wlValues.subject,
                student: {
                  ...dataValues,
                  ...loginValues,
                  emails,
                },
              });
            },
            reload: () =>
              (document.location =
                routes.waitinglist_student_successRoute.link()),
          });
          return (
            <div>
              {
                <div className={style.equal_columns}>
                  <div>{dataForm.render()}</div>
                  <div>
                    {loginForm.render()}

                    {wlForm.render()}

                    <div style="text-align: right; ">
                      {saveButton}

                      <a
                        href={loginRouteLink}
                        style="white-space: pre-line; display: block;"
                      >
                        {"Is de persoon die je inschrijft al gekend bij ons? \nLog dan hier in."}
                      </a>
                    </div>
                  </div>
                </div>
              }
            </div>
          );
        }
      });
    } else {
      const students = opts.students;
      const form = new Form({})
        .addField("student", [], async () => {
          if (students.length > 1) {
            return radioField({
              initial: students[0],
              values: students,
              show: (s) => renderFirstNameLastName(s),
              render: (s, selected, onclick) => {
                return (
                  <a onclick={onclick} style="margin-bottom: 16px;">
                    <div
                      className={
                        style.card +
                        style.card_with_icon +
                        (selected ? style.is_selected : "")
                      }
                    >
                      <i className={FA.icon_user_solid}></i>
                      {s}
                    </div>
                  </a>
                );
              },
              containerClass: style.grid_card_small,
            });
          } else {
            return constantField(students[0]);
          }
        })
        .addField("waitinglist", [], async () => makeWaitinglistForm(opts))
        .build();

      return (
        <div>
          <section>{form.render()}</section>
          <section style="text-align: right">
            {renderSaveButton({
              formS: form.s,
              save: ({ student, waitinglist }) =>
                rpc(routes.saveWaitinglistExistingStudentSpec, undefined, {
                  studentid: student.id,
                  subject: waitinglist.subject,
                }),
              reload: () =>
                (document.location =
                  routes.waitinglist_student_successRoute.link()),
            })}
          </section>
        </div>
      );
    }
  })();

  return [header, <main style={mainStyle}>{content}</main>];
}

function makeWaitinglistForm(opts: Pick<opts, "subject" | "allFormulas">) {
  const form = new Form({})
    .addField("subject", [], async () => {
      if (opts.subject.tag === "private") {
        const subject = opts.subject;
        return new Form({})
          .addField("tag", [], () => constantField("private" as const))
          .addField("instrumentid", [], () =>
            constantField(subject.instrument.id),
          )
          .addField("formula", [], () => {
            const values = opts.allFormulas
              .filter(
                (f) =>
                  f.showonpublicplanning === true &&
                  f.type_.tag === "Private" &&
                  f.planningmode === "Weekly" &&
                  (f.instruments || []).some(
                    (instrid) => instrid === subject.instrument.id,
                  ),
              )
              .orderBy((f) => f.description);
            // multiple?
            return selectBox({
              values: values,
              initial: values[0],
              show: (f) => f.description,
              label: "Formule",
            });
          })
          .addField("timeranges", [], () =>
            // TODO!!!
            constantField(
              [] as {
                starttime: LocalTime;
                endtime: LocalTime;
                weekday: DayOfWeek;
              }[],
            ),
          )
          .addField("internalteacherid", [], () => {
            // TODO!!
            return constantField(null as null | internalteacherid);
          })
          .build();
      } else {
        return constantField({
          tag: "lessongroup" as const,
          lessongroupid: opts.subject.lessongroupid,
        });
      }
    })

    .build();

  return form;
}
